import React, {ReactNode, useCallback, useMemo} from "react";
import {useFieldControl} from "../control";
import {FormHelperText, ToggleButton, ToggleButtonGroup} from "@mui/material";

export interface FieldToggleButtonsOption<
    Value,
    Multiple extends boolean | undefined
> {
    key: string | number,
    label: ReactNode,
    value: FieldToggleButtonsValue<Value, Multiple>,
}

export type FieldToggleButtonsValue<Value, Exclusive> = Exclusive extends false
    ? Value extends Array<infer Inner> ? Inner : never
    : Value;

export interface FieldToggleButtonsProps<Value, Exclusive extends boolean | undefined> {
    options: Array<FieldToggleButtonsOption<Value, Exclusive>>
    exclusive?: Exclusive;
    sort?: Exclusive extends false ? (a: FieldToggleButtonsValue<Value, Exclusive>, b: FieldToggleButtonsValue<Value, Exclusive>) => number : never;
}

const FieldToggleButtons = <
    Value,
    Exclusive extends boolean = true,
>(props: FieldToggleButtonsProps<Value, Exclusive>) => {
    const {setValue, ...fieldControl} = useFieldControl<FieldToggleButtonsValue<Value, Exclusive>>();

    const value = useMemo(() => {
        if (!props.exclusive) {
            return fieldControl.value ?? [];
        }
        return fieldControl.value ?? "";
    }, [fieldControl.value, props.exclusive]);

    /**
     * Handles the value change of the field.
     */
    const onValueChanged = useCallback((_: React.MouseEvent<HTMLElement>, value: FieldToggleButtonsValue<Value, Exclusive>) => {
        if (!props.exclusive) {
            if (!!props.sort) {
                (value as Array<any>).sort(props.sort);
            }
            setValue(value);
        } else {
            setValue(value)
        }
    }, [props.exclusive, props.sort, setValue]);

    return (
        <>
            <ToggleButtonGroup
                fullWidth
                exclusive={props.exclusive ?? true}
                size={'small'}
                disabled={fieldControl.disabled}
                value={value}
                color={'primary'}
                orientation="horizontal"
                sx={{height: 40, ...(fieldControl.erroneous && {borderColor: 'error.main'})}}
                onChange={onValueChanged}
            >
                {
                    props.options.map((option) => (
                        <ToggleButton key={option.key} value={option.value as any}>
                            {option.label}
                        </ToggleButton>
                    ))
                }
            </ToggleButtonGroup>
            {
                !!fieldControl.helperText &&
                <FormHelperText variant={'outlined'} error={fieldControl.erroneous}>
                    {fieldControl.helperText}
                </FormHelperText>
            }
        </>
    );
}

export default FieldToggleButtons;
